import { AppPageLayout } from "@/components/common/AppPageLayout";
import { publicFormRoutePaddingClass } from "@/features/pages/components/formPageLayout";
import { useSelectFormById } from "@/features/forms/api/selectFormById";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import FormPage from "@/features/pages/components/FormPage";
import { useMsAdlFormPageContext } from "@/features/pages/hooks/useMsAdlFormPageContext";
import useFormPage from "@/features/pages/hooks/useFormPage";
import { readPublicFormSide } from "@/features/pages/utils/publicFormSide";
import { formatTrackerLateralityBanner } from "@/features/pages/utils/trackerLateralityBanner";
import { useEntryValuesStore } from "@/features/inputs/hooks/useEntryValuesStore";
import { useProfileByIdQuerySafe } from "@/features/profile/api/selectProfile";
import {
    submitValidatorFormReview,
    validatorReviewAnswers,
} from "@/features/validations/utils/submitValidatorFormReview";
import { readValidatorFormReview } from "@/features/validations/utils/validatorAutoSubmitEmail";
import { useAuth } from "@/utils/auth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "@patient-watch/ui/sonner";
import { useState } from "react";
import { trackEvent } from "@/utils/trackEvent";
import Spinner from "@patient-watch/ui/spinner";

const Page = () => {
    const { pageIndex, formId, diaryId, entryId, trackerId } = Route.useParams();
    const navigate = useNavigate();
    const auth = useAuth();
    const { data: signedInProfile } = useProfileByIdQuerySafe(auth.isAuthenticated ? auth.id : null);
    const getEntryValues = useEntryValuesStore((state) => state.getEntryValues);
    const [isFinishingReview, setIsFinishingReview] = useState(false);
    const { locale } = useAppLocale();
    const { data: form } = useSelectFormById(formId, locale);
    const numericPageIndex = Number(pageIndex);
    const lateralityBanner = form?.is_side_specific
        ? formatTrackerLateralityBanner(readPublicFormSide(entryId), locale)
        : null;

    const {
        currentPage,
        pageCount,
        msAdlSelectionInputId,
        renderPageIndex,
        isLastPageOverride,
        progressPageIndex,
        progressPageTotal,
    } = useMsAdlFormPageContext(form, numericPageIndex);

    const {
        form: reactHookForm,
        onSubmit,
        prevPage,
        nextPage,
        isLastPage,
        visitedPageCount,
    } = useFormPage({
        pageCount,
        inputs: currentPage?.inputs ?? [],
        nextEntry: null,
        form,
        formType: "tracker",
        isLastPageOverride,
        skipDbWrite: true,
        nextPageFn: (newPageIndex) => {
            navigate({
                to: Route.to,
                params: {
                    formId,
                    diaryId,
                    entryId,
                    trackerId,
                    pageIndex: String(newPageIndex),
                },
                resetScroll: false,
            });
        },
        prevPageFn: (newPageIndex) => {
            navigate({
                to: Route.to,
                params: {
                    formId,
                    diaryId,
                    entryId,
                    trackerId,
                    pageIndex: String(newPageIndex),
                },
                resetScroll: false,
            });
        },
        successFn: () => {
            navigate({
                to: Route.to,
                params: {
                    formId,
                    diaryId,
                    entryId,
                    trackerId,
                    pageIndex: String(numericPageIndex + 1),
                },
            });
        },
        completeFn: () => {
            trackEvent("form_completed", { form_id: formId });
            const review = readValidatorFormReview();
            if (review?.formId === formId) {
                const email = review.email || signedInProfile?.email?.trim() || "";
                if (!email) {
                    toast.error("Could not save these answers. Stay on this page and submit again.");
                    return;
                }
                setIsFinishingReview(true);
                void submitValidatorFormReview({
                    validationId: review.validationId,
                    formId,
                    entryId,
                    email,
                    entryValues: validatorReviewAnswers(entryId, getEntryValues()),
                })
                    .then((validationId) => {
                        void navigate({
                            to: "/validate/$validationId/forms/$formId/results",
                            params: { validationId, formId },
                        });
                    })
                    .catch(() => {
                        setIsFinishingReview(false);
                        toast.error("Could not save these answers. Stay on this page and submit again.");
                    });
                return;
            }
            navigate({
                to: "/forms/$formId/diary/$diaryId/tracker/$trackerId/entry/$entryId/complete",
                params: { formId, diaryId, trackerId, entryId },
            });
        },
    });

    if (isFinishingReview) {
        return (
            <section className="flex flex-col justify-center items-center gap-3 py-24">
                <Spinner className="w-10 h-10" />
                <p className="text-slate-500 text-sm">Saving your answers...</p>
            </section>
        );
    }

    if (!form || !currentPage) return null;

    return (
        <AppPageLayout width="md" padding="sm" className={publicFormRoutePaddingClass}>
            <FormPage
                pageIndex={numericPageIndex}
                form={form}
                route={Route}
                formType="tracker"
                reactHookForm={reactHookForm}
                onSubmit={onSubmit}
                prevPage={prevPage}
                nextPage={nextPage}
                isLastPage={isLastPageOverride ?? isLastPage}
                visitedPageCount={visitedPageCount}
                msAdlSelectionInputId={msAdlSelectionInputId}
                renderPageIndex={renderPageIndex}
                progressPageIndex={progressPageIndex}
                progressPageTotal={progressPageTotal}
                lateralityBanner={lateralityBanner}
            />
        </AppPageLayout>
    );
};

export const Route = createFileRoute("/forms/$formId/diary/$diaryId/tracker/$trackerId/entry/$entryId/page/$pageIndex")(
    {
        component: Page,
    }
);
